/**
 * Pool ledger — committed requirement against qualified capacity, per pool,
 * under both engine passes (D3).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md §6.
 * Math: src/lib/poolPressure.ts (pure). This renders; it decides nothing.
 *
 * Stephens' "individually rational initiatives, collectively impossible
 * portfolio" only becomes visible when both passes are on screen together, so
 * both are always shown — never a single blended number.
 *
 * An overcommitted pool flags every commitment drawing on it and says
 * "structurally impossible as committed". It does not say which one to cut:
 * the system never picks the victim.
 */

import {
  commitmentsOnOverloadedPools,
  type PoolPressureRow,
} from '../../../lib/poolPressure';
import { formatHours } from '../../../lib/commitmentState';

const STATE_STYLE: Record<PoolPressureRow['state'], string> = {
  within: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  at_threshold: 'text-[var(--mustard)]',
  over: 'text-[var(--coral)]',
  no_capacity_on_file: 'text-[var(--text-ter)]',
};

function Pass({
  title,
  caption,
  rows,
  nameOf,
}: {
  title: string;
  caption: string;
  rows: readonly PoolPressureRow[];
  nameOf: (itemId: string) => string;
}) {
  const flagged = commitmentsOnOverloadedPools(rows);
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
        {title}
      </div>
      <p className="text-xs text-[var(--text-ter)] mt-0.5">{caption}</p>
      <ul className="mt-2 space-y-2">
        {rows.length === 0 && (
          <li className="text-sm text-[var(--text-ter)]">No capacity pools on this scenario.</li>
        )}
        {rows.map((row) => (
          <li key={row.resourceType}>
            <div className="flex items-baseline gap-2">
              <span className="text-sm capitalize text-[var(--text-pri)]">{row.resourceType}</span>
              <span className="text-xs tabular-nums text-[var(--text-ter)]">
                {formatHours(row.requiredHours)}
                {row.utilization != null && ` / ${formatHours(row.capacityHours)}`}
              </span>
              {row.state === 'over' && (
                <span className={`text-xs font-medium ${STATE_STYLE.over}`}>
                  over by {formatHours(row.overByHours)}
                </span>
              )}
            </div>
            {row.utilization != null && (
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--hover-tint)]">
                <div
                  className={
                    row.state === 'over'
                      ? 'h-full bg-[var(--coral)]'
                      : row.state === 'at_threshold'
                        ? 'h-full bg-[var(--mustard)]'
                        : 'h-full bg-[var(--green)]'
                  }
                  style={{ width: `${Math.min(100, Math.round(row.utilization * 100))}%` }}
                />
              </div>
            )}
            <p className={`mt-1 text-xs ${STATE_STYLE[row.state]}`}>{row.sentence}</p>
          </li>
        ))}
      </ul>
      {flagged.size > 0 && (
        <p className="mt-3 text-xs text-[var(--coral)]">
          Drawing on a pool that cannot carry them:{' '}
          {[...flagged.keys()].map(nameOf).join(', ')}. Which one gives way is not a call this
          system makes.
        </p>
      )}
    </div>
  );
}

export default function PoolPressureLedger({
  optimistic,
  conservative,
  bufferPct,
  nameOf,
}: {
  optimistic: readonly PoolPressureRow[];
  conservative: readonly PoolPressureRow[];
  bufferPct: number;
  nameOf: (itemId: string) => string;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">
          What the pools can carry
        </h3>
        <span className="text-xs text-[var(--text-ter)]">
          Requirement from work types vs qualified capacity
        </span>
      </div>
      <div className="mt-4 flex flex-col gap-6 md:flex-row md:gap-8">
        <Pass
          title="Optimistic"
          caption="raw pools, raw estimates"
          rows={optimistic}
          nameOf={nameOf}
        />
        <Pass
          title="Conservative"
          caption={`pools less the ${bufferPct}% buffer`}
          rows={conservative}
          nameOf={nameOf}
        />
      </div>
    </div>
  );
}
